/**
 * Reconstrução de linhas visuais a partir dos text items do PDF.js.
 *
 * Os PDFs de pedido/NF são tabelas em duas ou mais colunas; concatenar os items
 * na ordem em que o PDF.js os entrega mistura as colunas e destrói a leitura.
 * Aqui os items são ordenados por coordenada (y desc, depois x), agrupados em
 * linhas por proximidade vertical e separados por ` | ` quando há um salto
 * horizontal — o que preserva a estrutura de colunas para os parsers.
 */

export interface PdfTextItem {
  str?: string;
  width?: number;
  hasEOL?: boolean;
  transform?: number[];
}

export function buildLayoutLines(items: PdfTextItem[]): string[] {
  const positioned = items
    .map(it => ({
      str: (it.str ?? '').trim(),
      x: Array.isArray(it.transform) ? Number(it.transform[4] ?? 0) : 0,
      y: Array.isArray(it.transform) ? Number(it.transform[5] ?? 0) : 0,
      width: Number(it.width ?? 0),
      hasEOL: Boolean(it.hasEOL),
    }))
    .filter(it => it.str);

  if (!positioned.length) return [];

  const sorted = [...positioned].sort((a, b) => {
    if (Math.abs(a.y - b.y) > 2) return b.y - a.y;
    return a.x - b.x;
  });

  const rows: Array<Array<(typeof sorted)[number]>> = [];
  sorted.forEach(item => {
    const lastRow = rows.at(-1);
    if (!lastRow) { rows.push([item]); return; }
    const avgY = lastRow.reduce((sum, rowItem) => sum + rowItem.y, 0) / lastRow.length;
    if (Math.abs(avgY - item.y) <= 2.5) lastRow.push(item);
    else rows.push([item]);
  });

  return rows.map(row => {
    const cols = [...row].sort((a, b) => a.x - b.x);
    let line = '';
    let prevEnd = 0;
    cols.forEach((col, idx) => {
      const gap = idx === 0 ? 0 : col.x - prevEnd;
      line += `${gap > 16 ? ' | ' : ' '}${col.str}`.trimStart();
      prevEnd = col.x + col.width;
      if (col.hasEOL) line += ' ';
    });
    return line.replace(/\s+\|\s+/g, ' | ').replace(/\s+/g, ' ').trim();
  }).filter(Boolean);
}
